import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import { ApprovalBadge, RowActions, PendingApprovalBanner, PendingCountBadge } from '../components/ApprovalActions';
import { formatCurrency, formatDate, today } from '../utils/helpers';

const empty = { vehicle_id:'', sale_id:'', trip_date:today(), start_km:0, end_km:0, from_location:'', to_location:'', driver_allowance:0, toll_charges:0, other_expenses:0, status:'completed', remarks:'' };

export default function Trips() {
  const [list, setList] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [sales, setSales] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get('/trips').then(r => setList(r.data.data || []));
  useEffect(() => {
    load();
    api.get('/vehicles').then(r => setVehicles(r.data.data || []));
    api.get('/sales').then(r => setSales(r.data.data || []));
  }, []);

  const save = async () => {
    if (!form.vehicle_id) return toast.error('Vehicle required');
    try {
      if (editId) await api.put('/trips/' + editId, form);
      else await api.post('/trips', form);
      toast.success('Saved'); setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error('Error'); }
  };
  const edit = (t) => { setForm({...empty, ...t}); setEditId(t.id); setOpen(true); };

  const totalKm = list.reduce((s, r) => s + parseInt(r.total_km||0), 0);
  const totalExp = list.reduce((s, r) => s + parseFloat(r.total_expense||0), 0);

  const pendingCount = list.filter(x => x.approval_status === 'pending').length;
  return (
    <div className="card">
      <div className="card-header">
        <h3>Trips ({list.length}) — Total KM: {totalKm} · Total Expense: {formatCurrency(totalExp)}<PendingCountBadge count={pendingCount}/></h3>
        <button className="btn btn-blue" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}><Plus size={14}/> New Trip</button>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Trip No</th><th>Date</th><th>Vehicle</th><th>From → To</th><th>KM</th><th className="td-num">Allowance</th><th className="td-num">Toll</th><th className="td-num">Other</th><th className="td-num">Total</th><th>Status</th><th>Approval</th><th>Actions</th></tr></thead>
          <tbody>
            {list.length ? list.map(t => (
              <tr key={t.id} style={t.approval_status==='pending'?{background:'#fffbeb'}:{}}>
                <td><strong>{t.trip_no}</strong></td>
                <td>{formatDate(t.trip_date)}</td>
                <td>{t.vehicle_no}
                  {t.transporter_name && <small style={{display:'block',color:'#64748b',fontSize:11}}>{t.transporter_name}</small>}
                </td>
                <td>{t.from_location} → {t.to_location}</td>
                <td>{t.total_km}</td>
                <td className="td-num">{formatCurrency(t.driver_allowance)}</td>
                <td className="td-num">{formatCurrency(t.toll_charges)}</td>
                <td className="td-num">{formatCurrency(t.other_expenses)}</td>
                <td className="td-num">{formatCurrency(t.total_expense)}</td>
                <td><span className={'badge ' + (t.status==='completed'?'badge-green':t.status==='ongoing'?'badge-orange':t.status==='planned'?'badge-blue':'badge-red')}>{t.status}</span></td>
                <td>
                  <RowActions endpoint="/trips" row={t} onChange={load} onEdit={edit}/>
                </td>
              </tr>
            )) : <tr><td colSpan="12" className="empty">No trips</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={()=>setOpen(false)} title={editId?'Edit Trip':'New Trip'} size="lg"
        footer={<>
          <button className="btn btn-gray" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save}>{editId?'Update':'Save'}</button>
        </>}>
        <div className="form-grid">
          <div className="form-group"><label>Vehicle *</label>
            <select value={form.vehicle_id} onChange={e=>setForm({...form, vehicle_id:e.target.value})}>
              <option value="">-- Select --</option>
              {vehicles.map(v=><option key={v.id} value={v.id}>{v.vehicle_no} — {v.driver_name}{v.transporter_name?` · ${v.transporter_name}`:''}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Date</label><input type="date" value={form.trip_date} onChange={e=>setForm({...form, trip_date:e.target.value})}/></div>
          <div className="form-group"><label>Link to Sale Invoice</label>
            <select value={form.sale_id} onChange={e=>setForm({...form, sale_id:e.target.value})}>
              <option value="">-- None --</option>
              {sales.map(s=><option key={s.id} value={s.id}>{s.invoice_no} — {s.ledger_name}</option>)}
            </select>
          </div>
          <div className="form-group"><label>From Location</label><input value={form.from_location} onChange={e=>setForm({...form, from_location:e.target.value})}/></div>
          <div className="form-group"><label>To Location</label><input value={form.to_location} onChange={e=>setForm({...form, to_location:e.target.value})}/></div>
          <div className="form-group"><label>Start KM</label><input type="number" value={form.start_km} onChange={e=>setForm({...form, start_km:e.target.value})}/></div>
          <div className="form-group"><label>End KM</label><input type="number" value={form.end_km} onChange={e=>setForm({...form, end_km:e.target.value})}/></div>
          <div className="form-group"><label>Driver Allowance</label><input type="number" value={form.driver_allowance} onChange={e=>setForm({...form, driver_allowance:e.target.value})}/></div>
          <div className="form-group"><label>Toll Charges</label><input type="number" value={form.toll_charges} onChange={e=>setForm({...form, toll_charges:e.target.value})}/></div>
          <div className="form-group"><label>Other Expenses</label><input type="number" value={form.other_expenses} onChange={e=>setForm({...form, other_expenses:e.target.value})}/></div>
          <div className="form-group"><label>Status</label>
            <select value={form.status} onChange={e=>setForm({...form, status:e.target.value})}>
              <option value="planned">Planned</option><option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option><option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="form-group full-width"><label>Remarks</label><textarea value={form.remarks} onChange={e=>setForm({...form, remarks:e.target.value})}/></div>
        </div>
      </Modal>
    </div>
  );
}
